'use client';

import React from 'react';
import { DateTime } from 'luxon';

import clsx from 'clsx';
import styles from './QuoteAltSuggestions.module.scss';
import { BzImage } from '@benzinga/image';
import { PostElapsed } from '@benzinga/news';
import { News } from '@benzinga/basic-news-manager';
import { DelayedQuote } from '@benzinga/quotes-manager';
import { Mover, Movers } from '@benzinga/movers-manager';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { formatTickerToURLFriendly } from '@benzinga/utils';

interface Props {
  symbol: string;
  similarSymbols: DelayedQuote[];
  news: News[];
  movers: Movers;
}

interface ListProps {
  title: string;
  emptyText: string;
  list: DelayedQuote[] | Mover[];
  showOrder?: boolean;
}

export const AltQuoteList: React.FC<ListProps> = ({ emptyText, list, showOrder = false, title }) => {
  return (
    <div className={styles.listWrapper}>
      <h2 className={styles.listHeader}>{title.toUpperCase()}</h2>
      <div className={styles.list}>
        {list?.length > 0 &&
          list.map((ticker, idx) => {
            return (
              <a className={styles.row} href={`/quote/${ticker.symbol?.replace('/USD', '-USD')}`} key={idx}>
                {showOrder && <div className="text-bzblue-900 text-xs">{idx + 1}.</div>}
                <div className={styles.nameWrapper}>
                  <div className={styles.ticker}>{ticker.symbol}</div>
                  <div className={styles.name}>
                    {ticker.type === 'CRYPTO' ? ticker.description : (ticker.name ?? ticker.companyName)}
                  </div>
                  <div className={styles.wrapperGradient}></div>
                </div>
                <div>
                  <div className={styles.price}>
                    $
                    {ticker.price?.toFixed(2) ??
                      ticker.lastTradePrice?.toFixed(2) ??
                      ticker.previousClosePrice?.toFixed(2)}
                  </div>
                  {typeof ticker.changePercent === 'number' && (
                    <div className={clsx(styles.percent, ticker.changePercent > 0 ? styles.positive : styles.negative)}>
                      {ticker.changePercent?.toFixed(2)}%
                    </div>
                  )}
                </div>
              </a>
            );
          })}
        {list?.length === 0 && <div className={clsx(styles.row, styles.empty)}>{emptyText}</div>}
      </div>
    </div>
  );
};

export const SessionDateRange = ({ fromDate, toDate }) => {
  const formatToSessionDate = (date: Date) => {
    const sessionDate = date
      ? DateTime.fromJSDate(date).setZone('America/New_York').toFormat('MMM d, yyyy h:mma ZZZZ')
      : null;

    return sessionDate;
  };
  const sessionFromDate = fromDate ? formatToSessionDate(new Date(fromDate)) : null;
  const sessionToDate = toDate ? formatToSessionDate(new Date(toDate)) : null;

  return (
    <div className="text-xs text-gray-600 text-right w-full mt-1">
      Session: {sessionFromDate} - {sessionToDate}
    </div>
  );
};

export const QuoteNewsGrid = ({ news, title }: { title: string; news: News[] }) => {
  return (
    <div className={styles.newsWrapper}>
      <div className={styles.newsHeader}>{title}</div>
      <div className={clsx(styles.newsList, 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3')}>
        {news?.length > 0 &&
          news.map((article, idx) => {
            return (
              <div className={styles.card} key={idx}>
                <a
                  className={styles.articleTitle}
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(article.title ?? '') }}
                  href={article.url}
                ></a>
                <div className={styles.cardFooter}>
                  <div className={styles.articleTopic}>{article.channels?.[0]?.name}</div>
                  <div className={styles.articleTimeAgo}>
                    <PostElapsed created={article.updated ?? ''} />
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export const QuoteAltSuggestions: React.FC<Props> = ({ movers, news, similarSymbols, symbol }) => {
  const oopsText = 'oops!';
  const notFoundText = 'The page you were looking for could not be found...';
  const popularNews = 'Popular News';

  return (
    <div className={styles.wrapper}>
      <div className={styles.banner}>
        <div>
          <div className={styles.oops}>{oopsText}</div>
          <div className={styles.text}>{notFoundText}</div>
        </div>
        <div className={styles.icon}>
          <BzImage height={56} src="/next-assets/images/not-found-folder.svg" width={78} />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AltQuoteList
          emptyText="No similar symbols found"
          list={similarSymbols}
          title={`Symbol Similar to: ${formatTickerToURLFriendly(symbol)?.toUpperCase()}`}
        />
        <div>
          <AltQuoteList
            emptyText="Data is not available at this time"
            list={movers?.gainers ?? []}
            title="Top Gaining Stocks"
          />
          <SessionDateRange fromDate={movers?.fromDate} toDate={movers?.toDate} />
        </div>
      </div>
      <QuoteNewsGrid news={news} title={popularNews} />
    </div>
  );
};
